import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { isValidPhoneFormat } from "../../utils/phone";
import { toast } from "react-toastify";
import {
  Building2,
  MapPin,
  ChevronLeft,
  Save,
  Phone,
  FileText,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getCompanyById,
  updateCompany,
} from "../../store/slices/superAdminCompanySlice";
import { withVersion } from "../../utils/concurrency";
import type { CompanyType } from "../../types";
import { superAdminCompanyAPI } from "../../services/api";
import { FormField, Input, Select } from "../../components/UI/FormField";
import Button from "../../components/UI/Button";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Country {
  _id: string;
  name: string;
  code: string;
}

interface EditFormValues {
  name: string;
  companyType: CompanyType;
  phone?: string;
  registrationOrLicenseNumber?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    postalcode?: string;
    country?: string;
  };
}

// ─── Validation Schema ────────────────────────────────────────────────────────

const schema = yup.object({
  name: yup
    .string()
    .required("Company name is required")
    .max(200, "Max 200 characters"),
  companyType: yup
    .string()
    .required("Company type is required")
    .oneOf(["forwarder", "shipper"], "Select forwarder or shipper"),
  phone: yup
    .string()
    .optional()
    .test(
      "phone-format",
      "Enter a valid phone number — digits, optionally with +, spaces, brackets or dashes",
      (value) => isValidPhoneFormat(value),
    ),
  registrationOrLicenseNumber: yup.string().optional(),
  address: yup
    .object({
      street: yup.string().optional(),
      city: yup.string().optional(),
      state: yup.string().optional(),
      postalcode: yup.string().optional(),
      country: yup.string().optional(),
    })
    .optional(),
});

// ─── Component ────────────────────────────────────────────────────────────────

const EditCompanyPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentCompany, loading } = useAppSelector(
    (state) => state.superAdminCompanies,
  );

  const [countries, setCountries] = useState<Country[]>([]);
  const [hasFetched, setHasFetched] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<EditFormValues>({
    resolver: yupResolver(schema as any),
  });

  // Load company if not already in state
  useEffect(() => {
    if (id) {
      setHasFetched(false);
      dispatch(getCompanyById(id)).finally(() => setHasFetched(true));
    }
  }, [dispatch, id]);

  // Pre-fill form once company data is available
  useEffect(() => {
    if (currentCompany) {
      reset({
        name: currentCompany.name,
        companyType: currentCompany.companyType || "forwarder",
        phone: currentCompany.phone || "",
        registrationOrLicenseNumber:
          currentCompany.registrationOrLicenseNumber || "",
        address: {
          street: currentCompany.address?.street || "",
          city: currentCompany.address?.city || "",
          state: currentCompany.address?.state || "",
          postalcode: currentCompany.address?.postalcode || "",
          // country may be a populated object or a plain ID string
          country:
            (currentCompany.address?.country as any)?._id ||
            currentCompany.address?.country ||
            "",
        },
      });
    }
  }, [currentCompany, reset]);

  // Load countries
  useEffect(() => {
    superAdminCompanyAPI
      .getCountries()
      .then((res) => setCountries(res.data.data.countries || []))
      .catch(() => toast.error("Failed to load countries"));
  }, []);

  // ── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Strip empty strings / nulls so Mongoose doesn't receive "" for country ObjectId.
   */
  const sanitizeAddress = (
    address?: EditFormValues["address"],
  ): EditFormValues["address"] | undefined => {
    if (!address) return undefined;
    const cleaned = Object.fromEntries(
      Object.entries(address as Record<string, unknown>).filter(
        ([, v]) => v !== "" && v !== null && v !== undefined,
      ),
    ) as EditFormValues["address"];
    return Object.keys(cleaned as object).length > 0 ? cleaned : undefined;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const onSubmit = async (data: EditFormValues) => {
    if (!id) return;

    const payload = {
      name: data.name,
      companyType: data.companyType,
      phone: data.phone || undefined,
      registrationOrLicenseNumber:
        data.registrationOrLicenseNumber || undefined,
      address: sanitizeAddress(data.address),
    };

    try {
      await dispatch(
        updateCompany({ id, data: withVersion(payload, currentCompany) }),
      ).unwrap();
      toast.success("Company updated successfully");
      navigate(`/companies/${id}`);
    } catch (err: any) {
      toast.error(err || "Failed to update company");
    }
  };

  // ── Loading / not-found states ─────────────────────────────────────────────

  if (loading || !hasFetched) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!currentCompany) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Company not found
          </h3>
          <div className="mt-6">
            <Link to="/companies">
              <Button variant="primary">Back to Companies</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Link
            to={`/companies/${id}`}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Edit Company
            </h1>
            <p className="text-sm text-gray-500">{currentCompany.name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-6">
            {/* ── Company Information ─────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  <h2 className="text-base font-semibold text-gray-900">
                    Company Information
                  </h2>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Subdomain cannot be changed after creation
                </p>
              </div>

              <div className="p-3 space-y-1">
                {/* Name */}
                <FormField
                  layout="inline"
                  size="sm"
                  label="Company Name"
                  required
                  error={errors.name?.message}
                >
                  <Input
                    size="sm"
                    {...register("name")}
                    placeholder="e.g. Acme Logistics OÜ"
                    error={!!errors.name}
                  />
                </FormField>

                {/* Company type */}
                <FormField
                  layout="inline"
                  size="sm"
                  label="Company Type"
                  required
                  error={errors.companyType?.message}
                >
                  <Select
                    size="sm"
                    {...register("companyType")}
                    error={!!errors.companyType}
                  >
                    <option value="forwarder">
                      Forwarder — sells transport
                    </option>
                    <option value="shipper">Shipper — buys transport</option>
                  </Select>
                </FormField>

                {/* Subdomain — read-only */}
                <FormField layout="inline" size="sm" label="Subdomain">
                  <Input
                    size="sm"
                    value={currentCompany.subdomain}
                    disabled
                    className="bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </FormField>

                {/* Phone */}
                <FormField
                  layout="inline"
                  size="sm"
                  label="Phone Number"
                  error={errors.phone?.message}
                >
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      size="sm"
                      {...register("phone")}
                      placeholder="+372 5123 4567"
                      className="pl-9"
                      error={!!errors.phone}
                    />
                  </div>
                </FormField>

                {/* Reg number */}
                <FormField
                  layout="inline"
                  size="sm"
                  label="Registration / License Number"
                  error={errors.registrationOrLicenseNumber?.message}
                >
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      size="sm"
                      {...register("registrationOrLicenseNumber")}
                      placeholder="e.g. REG-2024-001234"
                      className="pl-9"
                      error={!!errors.registrationOrLicenseNumber}
                    />
                  </div>
                </FormField>
              </div>
            </div>

            {/* ── Address ─────────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  <h2 className="text-base font-semibold text-gray-900">
                    Company Address
                  </h2>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  All address fields are optional
                </p>
              </div>

              <div className="p-3 space-y-1">
                <FormField
                  layout="inline"
                  size="sm"
                  label="Street"
                  error={errors.address?.street?.message}
                >
                  <Input
                    size="sm"
                    {...register("address.street")}
                    placeholder="e.g. Pärnu maantee 15"
                    error={!!errors.address?.street}
                  />
                </FormField>

                <FormField
                  layout="inline"
                  size="sm"
                  label="City"
                  error={errors.address?.city?.message}
                >
                  <Input
                    size="sm"
                    {...register("address.city")}
                    placeholder="e.g. Tallinn"
                    error={!!errors.address?.city}
                  />
                </FormField>

                <FormField
                  layout="inline"
                  size="sm"
                  label="State / County"
                  error={errors.address?.state?.message}
                >
                  <Input
                    size="sm"
                    {...register("address.state")}
                    placeholder="e.g. Harju"
                    error={!!errors.address?.state}
                  />
                </FormField>

                <FormField
                  layout="inline"
                  size="sm"
                  label="Postal Code"
                  error={errors.address?.postalcode?.message}
                >
                  <Input
                    size="sm"
                    {...register("address.postalcode")}
                    placeholder="e.g. 10141"
                    error={!!errors.address?.postalcode}
                  />
                </FormField>

                <FormField
                  layout="inline"
                  size="sm"
                  label="Country"
                  error={errors.address?.country?.message}
                >
                  <Select
                    size="sm"
                    {...register("address.country")}
                    error={!!errors.address?.country}
                    placeholder="Select a country"
                  >
                    {countries.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name} ({c.code})
                      </option>
                    ))}
                  </Select>
                </FormField>
              </div>
            </div>

            {/* ── Footer ──────────────────────────────────────────────────── */}
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(`/companies/${id}`)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                isLoading={loading}
                disabled={!isDirty || loading}
                icon={!loading ? <Save className="w-4 h-4" /> : undefined}
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCompanyPage;
